import { GraphQLSchema } from 'graphql';
import * as monacoEditor from 'monaco-editor';
import graphqlPlugin from 'prettier/plugins/graphql';
import { format } from 'prettier/standalone';
import React, { useCallback } from 'react';

import { executeEdits } from 'src/app/graph/explorerPage/explorerDocs/getFieldToggle/executeEdits';
import {
  AddedArgument,
  getVariableEdits,
} from 'src/app/graph/explorerPage/explorerDocs/getFieldToggle/getInsertion';
import { FieldTimingsPercentile } from 'src/app/graph/explorerPage/explorerSettings/useExplorerSettings';
import {
  getDocumentDefinitions,
  getInlineArgumentRanges,
} from 'src/app/graph/explorerPage/helpers/graphqlParseHelpers';
import { initializeGraphqlDeemphasizedRanges } from 'src/app/graph/explorerPage/helpers/monacoHelpers';
import { JSONObject } from 'src/app/graph/explorerPage/helpers/types';
import { useDerivedEditorValue } from 'src/app/graph/explorerPage/hooks/useDerivedEditorValue';
import { ExplorerResult } from 'src/app/graph/explorerPage/hooks/useExplorerState/useExplorerResponses';
import { useInlineClassRangesEffect } from 'src/app/graph/explorerPage/hooks/useInlineClassRangesEffect';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { raw as IconErrorSolid } from 'src/assets/monaco-icon-error-solid.svg';
import { StudioEditorContainer } from 'src/hooks/useStudioEditor';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { sha256 } from 'src/lib/apollo/hash';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { getRootTypeNames } from 'src/lib/graphql/graphqlHelpers';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { ExplorerBehavioralFlags } from '../../explorerBehavioralFlags';
import { EditorBottomTab } from '../../helpers/EditorBottomTab';
import { useEditorModel } from '../../hooks/useEditorModel';
import { EditorTabState } from '../../hooks/useExplorerState/useEditorTabState/useEditorTabState';
import { EditorRefactors } from '../editorRefactors';

import { codeActionProviderFactory } from './codeActionProviderFactory';
import { DefinitionMenu } from './definitionMenu/DefinitionMenu';
import { ResponseHints } from './responseHints/ResponseHints';
import { schemaCompletionProviderFactory } from './schemaCompletionProviderFactory';
import { schemaDefinitionProviderFactory } from './schemaDefinitionProviderFactory';
import { schemaHoverProviderFactory } from './schemaHoverProviderFactory';
import { SubgraphHints } from './subgraphHints/SubgraphHints';
import { TimingHints } from './timingHints/TimingHints';
import { TraceTimingHints } from './traceTimingHints/TraceTimingHints';

export const DOCUMENT_FORMATTING_EDIT_PROVIDER: monacoEditor.languages.DocumentFormattingEditProvider =
  {
    async provideDocumentFormattingEdits(document) {
      try {
        return [
          {
            range: document.getFullModelRange(),
            text: await format(document.getValue(), {
              parser: 'graphql',
              plugins: [graphqlPlugin],
            }),
          },
        ];
      } catch {
        return [
          {
            range: document.getFullModelRange(),
            text: document.getValue(),
          },
        ];
      }
    },
  };

export const DOCUMENT_RANGE_FORMATTING_EDIT_PROVIDER: monacoEditor.languages.DocumentRangeFormattingEditProvider =
  {
    async provideDocumentRangeFormattingEdits(document, range) {
      try {
        return [
          {
            range: document.getFullModelRange(),
            text: await format(document.getValue(), {
              parser: 'graphql',
              plugins: [graphqlPlugin],
              rangeStart: document.getOffsetAt(range.getStartPosition()),
              rangeEnd: document.getOffsetAt(range.getEndPosition()),
            }),
          },
        ];
      } catch {
        return [
          {
            range: document.getFullModelRange(),
            text: document.getValue(),
          },
        ];
      }
    },
  };

const EXTRACTABLE_AS_FRAGMENT_KEY = 'isExtractableAsFragment';
const INLINABLE_FRAGMENT_KEY = 'isInlinableFragment';

interface GraphqlEditorProps {
  className?: string;
  graphRef: GraphRef | null;
  graphURL?: null | string;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  variablesEditor: monacoEditor.editor.IStandaloneCodeEditor;
  schema: GraphQLSchema;
  hasSupergraphSchema: boolean;
  lintWarnings: {
    modelMarkers: monacoEditor.editor.IMarkerData[];
    modelDecorations?: monacoEditor.editor.IModelDeltaDecoration[];
  } | null;
  copyLinkToOperation: () => void;
  editorRefactorsRef: React.MutableRefObject<EditorRefactors | null>;
  setActiveEditorBottomTab: (tab: EditorBottomTab) => void;
  submitOperation: (args: {
    operationName: string | undefined;
    body: string;
    isSubscription: boolean;
  }) => void;
  submitForResponseHints: (
    operation: string,
  ) => Promise<JSONObject | undefined>;
  responseHints: GraphQLTypes.ResponseHints | null;
  fieldTimingsPercentile: FieldTimingsPercentile;
  shouldManageVariables: boolean;
  operationResult: ExplorerResult;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  editorTabState: Pick<
    EditorTabState,
    'selectedTab' | 'tabbedFileVariableValues'
  >;
  includeCookies: boolean;
  canBeEmbedded: boolean;
}

export const GraphqlEditor = ({
  className,
  graphRef,
  graphURL,
  editor,
  schema,
  hasSupergraphSchema,
  lintWarnings,
  copyLinkToOperation,
  editorRefactorsRef,
  variablesEditor,
  setActiveEditorBottomTab,
  submitOperation,
  submitForResponseHints,
  responseHints,
  fieldTimingsPercentile,
  shouldManageVariables,
  operationResult,
  explorerBehavioralFlags,
  editorTabState,
  includeCookies,
  canBeEmbedded,
}: GraphqlEditorProps) => {
  // Whenever `schema` changes, reassign the introspection ref. This will be
  // read (by reference) in the auto-complete engine, so it needs to be stored
  // in the ref.
  const schemaRef = React.useRef(schema);
  schemaRef.current = schema;
  const variablesEditorRef = React.useRef(variablesEditor);
  variablesEditorRef.current = variablesEditor;
  const shouldManageVariablesRef = React.useRef(shouldManageVariables);
  shouldManageVariablesRef.current = shouldManageVariables;

  const copyLinkToOperationRef = React.useRef(copyLinkToOperation);
  copyLinkToOperationRef.current = copyLinkToOperation;

  const trackCustomEvent = useTrackCustomEvent();

  useInlineClassRangesEffect({
    className: 'fs-exclude',
    editor,
    ranges: useDerivedEditorValue({
      editor,
      getValue: getInlineArgumentRanges,
      withPosition: false,
    }),
  });

  const model = useEditorModel(editor);
  React.useEffect(() => {
    if (!model) return;

    monacoEditor.editor.setModelMarkers(
      model,
      'apollo',
      lintWarnings?.modelMarkers ?? [],
    );

    const decorations = editor.createDecorationsCollection(
      lintWarnings?.modelDecorations,
    );
    // Clean up decorations
    return () => {
      decorations.clear();
    };
  }, [model, editor, lintWarnings]);

  // Monaco does not have a built in way to have preconditions based on
  // the result of the line clicked. What we do instead is temporarily add
  // an onDidChangeCursorPosition since it has the correct position at
  // that time. We could update the context key on each position update
  // but that seems like wasted work.
  const contextMenu = editor.getContribution<
    monacoEditor.editor.IEditorContribution & { _onContextMenu: () => void }
  >('editor.contrib.contextmenu');
  React.useEffect(() => {
    if (!contextMenu) return;
    const isExtractableAsFragment = editor.createContextKey<boolean>(
      EXTRACTABLE_AS_FRAGMENT_KEY,
      false,
    );
    const isInlinableFragment = editor.createContextKey<boolean>(
      INLINABLE_FRAGMENT_KEY,
      false,
    );

    const originalContextMenuMethod = contextMenu._onContextMenu;
    contextMenu._onContextMenu = (...args) => {
      const refreshFragmentRefactorKeys = () => {
        isInlinableFragment.set(
          !!editorRefactorsRef.current?.canInlineFragment(),
        );
        isExtractableAsFragment.set(
          !!editorRefactorsRef.current?.canExtractFragment(),
        );
      };

      // If there is no current selection, the current editor position will be
      // stale, and not yet updated to where the click happened. We will add
      // an onDidChangeCursorSelection hook to update the keys once that change
      // gets applied (partway through originalContextMenuMethod). If there is
      // a current selection and the right click happens inside it, the cursor
      // does not change position, so we want to call the refresh now as well.
      refreshFragmentRefactorKeys();
      const disposable = editor.onDidChangeCursorPosition(
        refreshFragmentRefactorKeys,
      );
      originalContextMenuMethod.apply(contextMenu, args);
      disposable.dispose();
    };
    return () => {
      contextMenu._onContextMenu = originalContextMenuMethod;
    };
  }, [editor, contextMenu, editorRefactorsRef]);

  React.useEffect(() => {
    // A command that can be called when accepting a completion. This is
    // useful to edit another monaco instance
    const suggestionAcceptedCommandID = editor.addCommand(
      monacoEditor.KeyCode.Unknown,
      (
        _editor: monacoEditor.editor.ICodeEditor,
        hasFields: boolean,
        addedArguments: AddedArgument[],
      ) => {
        const variableModel = variablesEditorRef.current?.getModel();
        if (
          addedArguments.length &&
          variableModel &&
          variablesEditorRef.current
        ) {
          executeEdits(
            {
              variableEdits: getVariableEdits(variableModel, addedArguments),
              type: 'insertArgument',
            },
            editor,
            variablesEditorRef.current,
            setActiveEditorBottomTab,
          );
        }
        if (hasFields) {
          editor.getAction('editor.action.triggerSuggest')?.run();
        }
      },
    );

    // This will only be called upon mounting; so we'll have to use a
    // `ref` to hold the schema so that when this callback is called,
    // it's response can be changed after instantiation
    const monacoDisposables = [
      ...(explorerBehavioralFlags.shouldShowCopyLink
        ? [
            editor.addAction({
              id: 'copy-document-link',
              label: 'Copy Link to Operation',
              contextMenuGroupId: 'sharing',
              contextMenuOrder: 3,
              run: () => copyLinkToOperationRef.current?.(),
            }),
          ]
        : []),
      editor.addAction({
        id: 'extract-variables',
        label: 'Extract Variables',
        contextMenuGroupId: 'refactor-variables',
        contextMenuOrder: 4,
        run: () => editorRefactorsRef.current?.extractVariables(),
      }),
      editor.addAction({
        id: 'inline-variables',
        label: 'Inline Variables',
        contextMenuGroupId: 'refactor-variables',
        contextMenuOrder: 4,
        run: () => editorRefactorsRef.current?.inlineVariables(),
      }),
      editor.addAction({
        id: 'extract-fragment',
        label: 'Extract Fragment',
        contextMenuGroupId: 'refactor-fragments',
        contextMenuOrder: 4,
        precondition: EXTRACTABLE_AS_FRAGMENT_KEY,
        run: () =>
          editorRefactorsRef.current?.extractFragment({
            isDeferFragment: false,
          }),
      }),
      editor.addAction({
        id: 'extract-defer-fragment',
        label: 'Extract @defer Fragment',
        contextMenuGroupId: 'refactor-defer-fragments',
        contextMenuOrder: 5,
        precondition: EXTRACTABLE_AS_FRAGMENT_KEY,
        run: async () => {
          editorRefactorsRef.current?.extractFragment({
            isDeferFragment: true,
          });

          const hash = await sha256(editor.getValue());
          trackCustomEvent({
            action: 'Click: Extract @defer Fragment',
            category: 'Explorer',
            // editor value hashed & truncated to 25 chars
            // to unique by operation & user id in charts
            secondaryLabel: hash.substring(0, 25),
          });
        },
      }),
      editor.addAction({
        id: 'wrap-with-defer-fragment',
        label: 'Wrap with inline @defer Fragment',
        contextMenuGroupId: 'refactor-defer-fragments',
        contextMenuOrder: 5,
        precondition: EXTRACTABLE_AS_FRAGMENT_KEY,
        run: async () => {
          editorRefactorsRef.current?.wrapWithDeferFragment();

          const hash = await sha256(editor.getValue());
          trackCustomEvent({
            action: 'Click: Wrap with inline @defer Fragment',
            category: 'Explorer',
            // editor value hashed & truncated to 25 chars
            // to unique by operation & user id in charts
            secondaryLabel: hash.substring(0, 25),
          });
        },
      }),
      editor.addAction({
        id: 'inline-fragment',
        label: 'Inline Fragment',
        contextMenuGroupId: 'refactor-fragments',
        contextMenuOrder: 4,
        precondition: INLINABLE_FRAGMENT_KEY,
        run: () => editorRefactorsRef.current?.inlineFragment(),
      }),

      monacoEditor.languages.registerCompletionItemProvider(
        'graphql',
        schemaCompletionProviderFactory({
          schemaRef,
          suggestionAcceptedCommandID,
          shouldManageVariablesRef,
        }),
      ),
      monacoEditor.languages.registerHoverProvider(
        'graphql',
        schemaHoverProviderFactory({ schemaRef }),
      ),
      monacoEditor.languages.registerDefinitionProvider(
        'graphql',
        schemaDefinitionProviderFactory({ schemaRef }),
      ),
      monacoEditor.languages.registerDocumentFormattingEditProvider(
        'graphql',
        DOCUMENT_FORMATTING_EDIT_PROVIDER,
      ),

      monacoEditor.languages.registerDocumentRangeFormattingEditProvider(
        'graphql',
        DOCUMENT_RANGE_FORMATTING_EDIT_PROVIDER,
      ),

      monacoEditor.languages.registerCodeActionProvider(
        'graphql',
        codeActionProviderFactory({ schemaRef }),
      ),
    ];

    return () =>
      monacoDisposables.forEach((disposable) => disposable.dispose());
  }, [
    editor,
    editorRefactorsRef,
    explorerBehavioralFlags,
    schema,
    setActiveEditorBottomTab,
    trackCustomEvent,
  ]);

  React.useEffect(() => {
    initializeGraphqlDeemphasizedRanges(editor, schema);
  }, [editor, schema]);

  React.useEffect(() => {
    // We don't want auto focus for the embedded version
    if (explorerBehavioralFlags.shouldAutoFocusEditor) {
      editor.focus();
    }
  }, [editor, explorerBehavioralFlags.shouldAutoFocusEditor]);

  const definitions = useDerivedEditorValue({
    editor,
    getValue: useCallback(
      (editorValue: string) => {
        const { fragmentDefinitions, operationDefinitions } =
          getDocumentDefinitions(editorValue, getRootTypeNames(schema));
        return (
          [...fragmentDefinitions, ...operationDefinitions]
            // only rerender when the ranges and kinds change
            .map(({ range, kind, name }) => ({ range, kind, name }))
            // Reverse the order, so last menues are first. This way the dropdown will cover the meatballs
            .sort((a, b) => b.range.startLineNumber - a.range.startLineNumber)
        );
      },
      [schema],
    ),
    withPosition: false,
  });

  return (
    <div className={className}>
      <style>
        {`
          /* TODO (jason) fix this upstream */
          /* The monaco glyph hover css is broken, add some size/padding */
          .monaco-editor-hover .hover-row > div:not(.hover-contents):not(.actions) {
            font-size: 12px;
            padding-top: 4px;
            padding-bottom: 4px;
            padding-left: 8px;
            padding-right: 8px;
            font-family: "var(--font-mono)";
          }
          .monaco-editor-hover .hover-row .hover-contents p {
            padding-top: 8px;
          }
          /* Add a red x beside line numbers */
          .xxx-monaco-syntax-error-glyph ~ .line-numbers {
            background-image: url(${IconErrorSolid});
            left: 0px !important;
            width: 100% !important;
            padding-right: 26px;
            background-repeat: no-repeat;
            background-size: 16px 16px;
            background-position: 7px 2px;
          }
        `}
      </style>
      {responseHints === GraphQLTypes.ResponseHints.SUBGRAPHS ? (
        graphRef && (
          <SubgraphHints
            editor={editor}
            schema={schema}
            hasSupergraphSchema={hasSupergraphSchema}
          />
        )
      ) : responseHints === GraphQLTypes.ResponseHints.SAMPLE_RESPONSES ? (
        <ResponseHints
          editor={editor}
          variablesEditor={variablesEditor}
          schema={schema}
          submitForResponseHints={submitForResponseHints}
        />
      ) : responseHints === GraphQLTypes.ResponseHints.TIMINGS ? (
        graphRef && (
          <TimingHints
            graphRef={graphRef}
            editor={editor}
            schema={schema}
            fieldTimingsPercentile={fieldTimingsPercentile}
          />
        )
      ) : responseHints === GraphQLTypes.ResponseHints.TRACE_TIMINGS ? (
        <TraceTimingHints
          editor={editor}
          schema={schema}
          operationResult={operationResult}
        />
      ) : responseHints === GraphQLTypes.ResponseHints.NONE ||
        responseHints === null ? null : (
        assertUnreachable(responseHints)
      )}
      <StudioEditorContainer editor={editor} className="h-full" />
      {definitions.map((definition, i) => (
        <DefinitionMenu
          graphURL={graphURL}
          editor={editor}
          definition={definition}
          variablesEditor={variablesEditor}
          submitOperation={submitOperation}
          schema={schema}
          setActiveEditorBottomTab={setActiveEditorBottomTab}
          // internal state will update properly if definition changes
          // so using index as key to avoid to much creating/removing dom nodes
          // eslint-disable-next-line react/no-array-index-key
          key={`${i} ${definition.name} ${editorTabState.selectedTab}`}
          editorRefactorsRef={editorRefactorsRef}
          canBeEmbedded={canBeEmbedded}
          shouldShowCopyLink={explorerBehavioralFlags.shouldShowCopyLink}
          includeCookies={includeCookies}
          editorTabState={editorTabState}
        />
      ))}
    </div>
  );
};
